import { useCallback } from 'react';

// Custom hook for GTM events
/**
 * Custom hook for sending events to Google Tag Manager.
 *
 * eventConfigs object has the following structure:
 * {
 * const eventConfigs = {
      pageOneSubmit: { // Equivalent to a dispatch action, e.g. sendEvent('pageOneSubmit')
        event: 'page_one_submit', // The trigger name of the actual event that will be sent to GTM
        name: 'Page One Submit', // The display name of the event
        nl_subscribe: false, // Allows setting defaults for parameters passed into sendEvent
    },
    ...otherEvents
  };
 * @param {Object} eventConfigs - The configuration object for events.
 * @returns {Function} - The sendEvent function.
 */
const useGTMEvents = (eventConfigs) => {
  // Memoize the sendEvent function to prevent re-creation on every render
  const sendEvent = useCallback(
    /**
     * Sends an event to Google Tag Manager.
     *
     * @param {string} eventName - The name of the event.
     * @param {Object} params - Additional parameters for the event.
     */
    (eventName, params = {}) => {
      const eventConfig = eventConfigs[eventName];
      if (!eventConfig) {
        console.warn(`No configuration found for event: ${eventName}`);
        return;
      }

      const eventObject = {
        event: eventConfig.event,
        [eventConfig.event]: {
          ...eventConfig,
          ...params, // Dynamically set any additional params
        },
      };

      if (typeof window.pushGTMEvent === 'function') {
        window.pushGTMEvent(eventObject);
      } else {
        console.error('window.pushGTMEvent is not a function');
      }
    },
    [],
  );

  return sendEvent;
};

export default useGTMEvents;
